import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type CurrentUserService from 'frontend/services/current-user';
import type IntlService from 'ember-intl/services/intl';
import { registerDestructor } from '@ember/destroyable';
import setDefaultOptions from 'date-fns/setDefaultOptions';
import config from 'frontend/config/environment';
import { fr } from 'date-fns/locale';
import type FastbootService from 'ember-cli-fastboot';
import type FavoriteService from 'frontend/services/favorite';
import type SessionService from 'frontend/services/session';
import * as Sentry from '@sentry/ember';
import { action } from '@ember/object';
import disableInFastboot from '@triptyk/ember-utils/decorators/disable-in-fastboot';

export default class Application extends Route {
  @service declare session: SessionService;
  @service declare intl: IntlService;
  @service declare currentUser: CurrentUserService;
  @service declare fastboot: FastbootService;
  @service declare favorite: FavoriteService;

  async beforeModel() {
    setDefaultOptions({ locale: fr });
    await this.setupMSWForDevelopment();
    this.intl.setLocale(['fr-fr']);
    await this.session.setup();
    await this.currentUser.load();

    if (!this.fastboot.isFastBoot) {
      Sentry.init({
        dsn: 'https://19320cf8d4a146c9b76aaa59c5b400b4@o1127730.ingest.sentry.io/4505436462645248',
        integrations: [],
        tracesSampleRate: 0.5,
        replaysSessionSampleRate: 0.5,
        replaysOnErrorSampleRate: 1.0,
        environment: config.environment,
        enabled:
          config.environment !== 'test' && config.environment !== 'development',
      });
      await this.loadFavorites();
    }
  }

  private async loadFavorites() {
    if (this.session.isAuthenticated) {
      await this.session.useOnlineAdapter();
    }
    await this.favorite.loadFavorites();
  }

  private async setupMSWForDevelopment() {
    if (
      config.environment === 'development' &&
      this.fastboot.isFastBoot === false
    ) {
      await setupMSW(this);
    }
  }

  @action
  @disableInFastboot
  willTransition() {
    // eslint-disable-next-line no-restricted-globals
    window.scrollTo(0, 0);
  }
}

async function setupMSW(context: object) {
  // @ts-expect-error
  let { worker } = await import('/mocks/index.js');
  await worker.start();
  registerDestructor(context, () => worker.stop());
}
